<template>
  <div class="page-key-behaviors" v-loading="loading">
    <portal to="layout-nav-title">关键行为数据</portal>

    <CustomerStructuresMenu />

    <h2 class="title" style="margin-top: 0">{{ title }}</h2>
    <el-form inline>
      <el-form-item label="报告类型">
        <PeriodSelect :period.sync="form.period" :date.sync="form.date" :periods="['day', 'week', 'month', 'year']" />
      </el-form-item>
      <el-form-item>
        <DialogLoading
          :visible="exportLoading"
          description="正在生成导出文件"
          :title="`导出处理中`"
        />
        <el-button type="primary" @click="handleExport">导出</el-button>
      </el-form-item>
    </el-form>

    <el-table
      v-if="keyBehaviorsColumns.length > 0 && keyBehaviorsRows.length > 0"
      :data="keyBehaviorsRows"
      border
      size="small"
      :span-method="mergeCell"
    >
      <el-table-column prop="root_team_name" label="队伍" min-width="100" />
      <el-table-column prop="child_team_name" label="小组" min-width="120" />
      <el-table-column prop="user_screen_name" label="业务员" min-width="100" />
      <el-table-column
        v-for="col in keyBehaviorsColumns"
        :key="col.id"
        :prop="col.name"
        :label="col.name"
        min-width="100"
      />
    </el-table>
  </div>
</template>

<script>
import { Vue, Component, Mixins, Watch } from 'vue-property-decorator';
import Utils from '@/components/utils.vue';
import PeriodSelect from '@/components/period-select.vue';
import CustomerStructuresMenu from './menu.vue';
import DialogLoading from '@/components/dialog-loading.vue';
import { exportFile } from '@/utils/export';
import moment from 'moment';
import _ from 'lodash';

@Component({
  components: {
    PeriodSelect,
    CustomerStructuresMenu,
    DialogLoading,
  },
})
export default class KeyBehaviors extends Mixins(Vue, Utils) {
  loading = false;
  exportLoading = false;
  data = {};
  form = {
    period: 'month',
    date: moment().startOf('month').format('YYYY-MM-DD'),
  };

  get title() {
    if (this.form.period === 'year') {
      return `${moment(this.date).format('YYYY年')}关键行为数据`;
    }
    if (this.form.period === 'month') {
      return `${moment(this.date).format('YYYY年MM月')}关键行为数据`;
    }
    if (this.form.period === 'day') {
      return `${moment(this.date).format('YYYY年MM月DD日')}关键行为数据`;
    }
    const today = moment();
    const endWeek = moment(this.date).endOf('isoWeek');
    const endDate = moment.min(endWeek, today);
    const format = 'YYYY年MM月DD日';
    return `${moment(this.date)
      .startOf('isoWeek')
      .format(format)} ~ ${endDate.format(format)}关键行为数据`;
  }

  get keyBehaviorsColumns() {
    return _.get(this.data, 'key_behaviors_columns', []);
  }

  get keyBehaviorsRows() {
    return _.get(this.data, 'key_behaviors', []);
  }

  get mergeRanges() {
    const ranges = { root_team_name: [], child_team_name: [] };
    let i = 0;
    while (i < this.keyBehaviorsRows.length) {
      let j = i;
      const rootTeam = this.keyBehaviorsRows[i].root_team_name;
      while (j < this.keyBehaviorsRows.length && this.keyBehaviorsRows[j].root_team_name === rootTeam) {
        j++;
      }
      ranges.root_team_name.push({ start: i, end: j - 1 });

      let k = i;
      while (k < j) {
        const teamName = this.keyBehaviorsRows[k].child_team_name;
        let l = k;
        while (l < j && this.keyBehaviorsRows[l].child_team_name === teamName) {
          l++;
        }
        ranges.child_team_name.push({ start: k, end: l - 1 });
        k = l;
      }
      i = j;
    }
    return ranges;
  }

  mergeCell({ column, rowIndex }) {
    const prop = column.property;
    if (prop !== 'root_team_name' && prop !== 'child_team_name') return;

    const ranges = this.mergeRanges[prop];
    for (const range of ranges) {
      if (rowIndex === range.start) {
        return { rowspan: range.end - range.start + 1, colspan: 1 };
      } else if (rowIndex > range.start && rowIndex <= range.end) {
        return { rowspan: 0, colspan: 0 };
      }
    }
  }

  async fetchData() {
    try {
      this.loading = true;
      this.data = await this.$request.get(
        '/reports/customer_structures/key_behaviors',
        { ...this.form }
      );
    } finally {
      this.loading = false;
    }
  }

  created() {
    const query = this.$route.query;
    Object.assign(
      this.form,
      _.pickBy(
        query,
        (value, key) => value && Object.keys(this.form).includes(key)
      )
    );
    this.debounceFetchData = _.debounce(this.fetchData, 500);
  }

  @Watch('form.period')
  @Watch('form.date')
  async handleChangeForm() {
    this.$router.replace({ query: { ...this.$route.query, ...this.form } }).catch(() => {});
  }

  @Watch('$route.query')
  async handleChangeQuery() {
    this.debounceFetchData();
  }

  mounted() {
    this.fetchData();
  }

  async handleExport() {
    try {
      this.exportLoading = true;
      await exportFile('reports/customer_structures/key_behaviors', {
        ...this.form,
        return_xlsx: true,
      });
    } finally {
      this.exportLoading = false;
    }
  }
}

export const route = [
  {
    path: '/reports/customer_structures/key_behaviors',
    name: 'reports/customer_structures.key_behaviors',
    component: KeyBehaviors,
    meta: {
      can: [],
    },
  },
];
</script>

<style lang="scss" scoped>
.page-key-behaviors {
  padding: 20px;
}
</style>
